/**
 * CreateRequestModal Component
 * Modal for creating a new transfer bond request
 * Uses OathUI components for consistent styling
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  createTransferBondRequest,
  getStates,
  getCountiesByState,
  getActiveAgentsByCounty,
} from '../../../services/oathTransferBondService';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  OathModal,
  OathButton,
  OathInput,
  OathSelect,
  OathAlert,
  OathSpinner,
} from '../../shared/OathUI';
import { REQUEST_FIELD_MAX_LENGTH } from './shared/requestFieldLimits';

const CreateRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useOathAuth();

  // Form state
  const [agencyId, setAgencyId] = useState('');
  const [submittedBy, setSubmittedBy] = useState('');
  const [stateCd, setStateCd] = useState('');
  const [countyId, setCountyId] = useState('');
  const [jail, setJail] = useState('');
  const [court, setCourt] = useState('');
  const [numBonds, setNumBonds] = useState('1');
  const [totalAmount, setTotalAmount] = useState('');
  const [defendantFirstName, setDefendantFirstName] = useState('');
  const [defendantLastName, setDefendantLastName] = useState('');

  // Data states
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [activeAgentsInfo, setActiveAgentsInfo] = useState(null);

  // Loading states
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success state
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [newRequestId, setNewRequestId] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Get requesting agencies from user
  const requestingAgencies = useMemo(() => {
    return (
      user?.agencies
        ?.filter((agency) => agency.canRequest)
        .map((agency) => ({
          value: agency.agencyId.toString(),
          label: `${agency.dbaAgencyName} (${agency.agencyId})`,
        })) || []
    );
  }, [user]);

  // Auto-select agency if only one
  useEffect(() => {
    if (requestingAgencies.length === 1 && !agencyId) {
      setAgencyId(requestingAgencies[0].value);
    }
  }, [requestingAgencies, agencyId]);

  // Fetch states on mount
  useEffect(() => {
    if (!isOpen) return;

    const fetchStates = async () => {
      setLoadingStates(true);
      const response = await getStates();
      if (response.success) {
        setStates(
          response.data.map((s) => ({
            value: s.stateCd,
            label: s.stateName.toUpperCase(),
          }))
        );
      }
      setLoadingStates(false);
    };

    fetchStates();
  }, [isOpen]);

  // Fetch counties when state changes
  useEffect(() => {
    if (!stateCd) {
      setCounties([]);
      setCountyId('');
      return;
    }

    const fetchCounties = async () => {
      setLoadingCounties(true);
      setCountyId('');
      setActiveAgentsInfo(null);

      const response = await getCountiesByState(stateCd);
      if (response.success) {
        setCounties(
          response.data
            .map((c) => ({
              value: c.countyId.toString(),
              label: c.county,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      }
      setLoadingCounties(false);
    };

    fetchCounties();
  }, [stateCd]);

  // Fetch active agents when county changes
  useEffect(() => {
    if (!countyId) {
      setActiveAgentsInfo(null);
      return;
    }

    const fetchAgents = async () => {
      setLoadingAgents(true);
      const response = await getActiveAgentsByCounty(countyId);
      if (response.success) {
        setActiveAgentsInfo(response.data);
      }
      setLoadingAgents(false);
    };

    fetchAgents();
  }, [countyId]);

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!agencyId) newErrors.agencyId = 'Agency is required';
    if (!submittedBy.trim()) newErrors.submittedBy = 'Your name is required';
    if (!stateCd) newErrors.stateCd = 'State is required';
    if (!countyId) newErrors.countyId = 'County is required';
    if (!jail.trim()) newErrors.jail = 'Jail location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);

    const requestData = {
      AgencyId: parseInt(agencyId),
      State: stateCd.toUpperCase(),
      CountyId: parseInt(countyId),
      Jail: jail.trim(),
      Court: court.trim(),
      NumBonds: parseInt(numBonds) || 1,
      TotalAmount: parseFloat(totalAmount) || 0,
      DefendantFirstName: defendantFirstName.trim().toUpperCase(),
      DefendantLastName: defendantLastName.trim().toUpperCase(),
      SubmittedBy: submittedBy.trim(),
      IsOnline: true,
    };

    const response = await createTransferBondRequest(requestData);

    if (response.success) {
      toast.success('Request submitted successfully.');
      setNewRequestId(response.transferBondTransactionId);
      setIsSubmitted(true);
      onSuccess?.();
    } else {
      toast.error(response.error || 'Failed to create request.');
      setErrors({ submit: response.error || 'Failed to create request' });
    }

    setIsSubmitting(false);
  };

  // Handle close
  const handleClose = () => {
    // Reset form
    setAgencyId(requestingAgencies.length === 1 ? requestingAgencies[0].value : '');
    setSubmittedBy('');
    setStateCd('');
    setCountyId('');
    setJail('');
    setCourt('');
    setNumBonds('1');
    setTotalAmount('');
    setDefendantFirstName('');
    setDefendantLastName('');
    setErrors({});
    setIsSubmitted(false);
    setNewRequestId(null);
    setActiveAgentsInfo(null);
    onClose();
  };

  // Handle view details
  const handleViewDetails = () => {
    if (newRequestId) {
      navigate(`/app/transfer-bonds/request/${newRequestId}`);
      handleClose();
    }
  };

  const selectedCounty = counties.find((c) => c.value === countyId);
  const isLouisiana = stateCd === 'LA';
  const isTexas = stateCd === 'TX';

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isSubmitted ? 'Request Submitted' : 'New Transfer Bond Request'}
      size="md"
      footer={!isSubmitted ? (
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            loading={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Creating...' : 'Create Request'}
          </OathButton>
        </div>
      ) : null}
    >
      {isSubmitted ? (
        // Success state
        <div className="text-center py-4">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Request Created!</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Your transfer bond request has been submitted. Active posting agents in the selected county will be notified.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            You can view the request details to see incoming quotes, or close this modal and check back later.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <OathButton onClick={handleViewDetails} className="w-full sm:w-auto sm:flex-1">
              View Details
            </OathButton>
            <OathButton variant="outline" onClick={handleClose} className="w-full sm:w-auto sm:flex-1">
              Close
            </OathButton>
          </div>
        </div>
      ) : (
        // Form
        <div className="space-y-5">
          {/* Instructions */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-sm">
            <div className="flex items-start gap-2 mb-2">
              <CheckCircleIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 dark:text-gray-400">Fill out required fields (*) for accurate processing.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-gray-600 dark:text-gray-400">If bond exceeds your underwriting authority, please call Bankers Surety.</span>
            </div>
          </div>

          {/* Submit error */}
          {errors.submit && (
            <OathAlert variant="error" message={errors.submit} />
          )}

          {/* Agency */}
          <OathSelect
            label="My Agency"
            value={agencyId}
            onChange={setAgencyId}
            options={requestingAgencies}
            placeholder="Select agency..."
            required
            error={errors.agencyId}
          />

          {/* Submitted By */}
          <OathInput
            label="Your Name"
            value={submittedBy}
            onChange={setSubmittedBy}
            placeholder="Name of person submitting"
            maxLength={REQUEST_FIELD_MAX_LENGTH.submittedBy}
            required
            error={errors.submittedBy}
          />

          {/* State */}
          <OathSelect
            label="State"
            value={stateCd}
            onChange={setStateCd}
            options={states}
            placeholder="Select state..."
            loading={loadingStates}
            required
            error={errors.stateCd}
          />

          {/* Louisiana warning */}
          {isLouisiana && (
            <OathAlert variant="info">
              <div className="flex items-start gap-2">
                <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">
                  You may need to pay state tax fees upfront. You may proceed with submitting this request.
                </p>
              </div>
            </OathAlert>
          )}

          {/* County */}
          <OathSelect
            label="County"
            value={countyId}
            onChange={setCountyId}
            options={counties}
            placeholder="Select county..."
            disabled={!stateCd}
            loading={loadingCounties}
            required
            error={errors.countyId}
          />

          {/* Harris County, TX — contact Bankers to finalize */}
          {stateCd === 'TX' && String(countyId) === '55856' && (
            <OathAlert variant="warning">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium mb-1">
                    Harris County, Texas requires direct coordination with Bankers
                  </p>
                  <p>
                    You can still request a quote, but you'll need to contact Bankers directly to
                    complete the post.
                  </p>
                </div>
              </div>
            </OathAlert>
          )}

          {/* County warning */}
          {countyId && (
            <OathAlert variant="warning">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">
                  Please ensure you're familiar with the court and forfeiture rules for this county before proceeding.
                </p>
              </div>
            </OathAlert>
          )}

          {/* Active agents info */}
          {countyId && !loadingAgents && activeAgentsInfo && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm">
              {activeAgentsInfo.numberOfAgencies > 0 ? (
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>{selectedCounty?.label}</strong> county has{' '}
                  <strong>{activeAgentsInfo.numberOfAgencies}</strong> agencies who can post.{' '}
                  <strong>{activeAgentsInfo.numberOfContacts}</strong> will be notified immediately.
                  {activeAgentsInfo.numberOfContacts === 0 && (
                    <span className="text-gray-600 dark:text-gray-400">
                      {' '}Since no agencies are currently active, Bankers will be notified immediately.
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-gray-700 dark:text-gray-300">
                  Currently, no agencies are available to post in <strong>{selectedCounty?.label}</strong> County.
                  Please submit your request and Bankers will be notified immediately to assist you.
                </p>
              )}
            </div>
          )}

          {loadingAgents && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <OathSpinner size="sm" /> Loading active agents...
            </div>
          )}

          {/* Jail */}
          <OathInput
            label="Jail"
            value={jail}
            onChange={setJail}
            placeholder="Jail location"
            maxLength={REQUEST_FIELD_MAX_LENGTH.jail}
            required
            error={errors.jail}
          />

          {/* Court */}
          <OathInput
            label="Courthouse"
            value={court}
            onChange={setCourt}
            placeholder="Courthouse (optional)"
            maxLength={REQUEST_FIELD_MAX_LENGTH.court}
          />

          {/* Number of Bonds */}
          <OathInput
            label="Number of Bonds"
            type="number"
            value={numBonds}
            onChange={setNumBonds}
            placeholder="1"
            helperText={isTexas ? 'For accurate quotes in Texas, please provide the number of bonds.' : undefined}
          />

          {/* Total Bond Amount */}
          <OathInput
            label="Total Bond Amount"
            type="number"
            value={totalAmount}
            onChange={setTotalAmount}
            placeholder="0.00"
            prefix="$"
          />

          {/* Defendant Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <OathInput
              label="Defendant First Name"
              value={defendantFirstName}
              onChange={(val) => setDefendantFirstName(val.toUpperCase())}
              placeholder="FIRST NAME"
              maxLength={REQUEST_FIELD_MAX_LENGTH.defendantFirstName}
            />
            <OathInput
              label="Defendant Last Name"
              value={defendantLastName}
              onChange={(val) => setDefendantLastName(val.toUpperCase())}
              placeholder="LAST NAME"
              maxLength={REQUEST_FIELD_MAX_LENGTH.defendantLastName}
            />
          </div>
        </div>
      )}
    </OathModal>
  );
};

export default CreateRequestModal;
